import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalysesTable = ({ analyses, onSort, sortField, sortDirection }) => {
  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-base"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortField === field && (
          <Icon 
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
            size={14} 
            color="currentColor" 
          />
        )}
      </div>
    </th>
  );

  const getStatusConfig = (status) => {
    const configs = {
      complete: { color: 'bg-success/10 text-success', icon: 'CheckCircle2', label: 'Complete' },
      processing: { color: 'bg-primary/10 text-primary', icon: 'Loader2', label: 'Processing' },
      pending: { color: 'bg-warning/10 text-warning', icon: 'Clock', label: 'Pending' },
      error: { color: 'bg-error/10 text-error', icon: 'AlertCircle', label: 'Error' }
    };
    return configs?.[status] || configs?.pending;
  };

  const getVerdictColor = (verdict) => {
    const colors = {
      'Fair': 'text-success',
      'Aggressive': 'text-warning',
      'Risky': 'text-error',
      'Good Value': 'text-success'
    };
    return colors?.[verdict] || 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-sm overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <SortableHeader field="property">Property</SortableHeader>
              <SortableHeader field="user">User</SortableHeader>
              <SortableHeader field="status">Status</SortableHeader>
              <SortableHeader field="verdict">Verdict</SortableHeader>
              <SortableHeader field="confidence">Confidence</SortableHeader>
              <SortableHeader field="processingTime">Processing Time</SortableHeader>
              <SortableHeader field="createdAt">Created</SortableHeader>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {analyses?.map((analysis) => {
              const statusConfig = getStatusConfig(analysis?.status);
              return (
                <tr key={analysis?.id} className="hover:bg-muted/20 transition-base">
                  <td className="px-4 py-4">
                    <div className="max-w-xs">
                      <p className="text-sm font-medium text-foreground truncate">{analysis?.property}</p>
                      <p className="text-xs text-muted-foreground">{analysis?.suburb}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium flex-shrink-0">
                        {analysis?.user?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()}
                      </div>
                      <span className="text-sm text-foreground truncate">{analysis?.user}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig?.color}`}>
                      <Icon name={statusConfig?.icon} size={12} color="currentColor" />
                      {statusConfig?.label}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {analysis?.verdict ? (
                      <span className={`text-sm font-medium ${getVerdictColor(analysis?.verdict)}`}>
                        {analysis?.verdict}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {analysis?.confidence ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden min-w-[50px]">
                          <div 
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${analysis?.confidence}%` }}
                          />
                        </div>
                        <span className="text-sm text-data whitespace-nowrap">{analysis?.confidence}%</span>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground text-data">{analysis?.processingTime}</td>
                  <td className="px-4 py-4 text-sm text-foreground text-data">{analysis?.createdAt}</td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" iconName="Eye">
                        View
                      </Button>
                      <Button variant="ghost" size="sm" iconName="MoreVertical" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {analyses?.map((analysis) => {
          const statusConfig = getStatusConfig(analysis?.status);
          return (
            <div key={analysis?.id} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-2">{analysis?.property}</h4>
                  <p className="text-xs text-muted-foreground mb-2">{analysis?.suburb}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {analysis?.user?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()}
                    </div>
                    <span className="text-xs text-muted-foreground truncate">{analysis?.user}</span>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.color} flex-shrink-0 ml-2`}>
                  <Icon name={statusConfig?.icon} size={12} color="currentColor" />
                  {statusConfig?.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground text-caption mb-1">Verdict</p>
                  {analysis?.verdict ? (
                    <p className={`font-medium ${getVerdictColor(analysis?.verdict)}`}>{analysis?.verdict}</p>
                  ) : (
                    <p className="text-muted-foreground">-</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground text-caption mb-1">Processing Time</p>
                  <p className="text-foreground text-data">{analysis?.processingTime}</p>
                </div>
                {analysis?.confidence && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground text-caption mb-2">Confidence Score</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${analysis?.confidence}%` }}
                        />
                      </div>
                      <span className="text-sm text-data whitespace-nowrap">{analysis?.confidence}%</span>
                    </div>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground text-caption mb-1">Created</p>
                  <p className="text-foreground text-data">{analysis?.createdAt}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" iconName="Eye" className="w-full">
                View Details
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AnalysesTable;